import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";


const IconLocation = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
);

const IconPhone = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M2 7l10 7 10-7" strokeLinecap="round" />
  </svg>
);

const WatermarkSVG = () => (
  <svg className="contact-hero-watermark" viewBox="0 0 400 400" fill="none">
    <circle cx="200" cy="200" r="190" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="140" stroke="white" strokeWidth="0.5" />
    <circle cx="200" cy="200" r="90" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="40" stroke="white" strokeWidth="0.5" />
    {[0,30,60,90,120,150,180,210,240,270,300,330].map((a, i) => {
      const r = (a * Math.PI) / 180;
      return <line key={i} x1={200+40*Math.cos(r)} y1={200+40*Math.sin(r)} x2={200+190*Math.cos(r)} y2={200+190*Math.sin(r)} stroke="white" strokeWidth="0.5" />;
    })}
  </svg>
);

export default function ContactUS() {
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", phone: "", subject: "", message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const heroRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const ruleRef = useRef(null);
  const subRef = useRef(null);
  const infoRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (typeof gsap === "undefined") return;
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    tl.to(eyebrowRef.current, { opacity: 1, duration: 0.6 }, 0.1)
      .to(titleRef.current, { opacity: 1, y: 0, duration: 0.8 }, 0.25)
      .to(ruleRef.current, { opacity: 1, scaleX: 1, duration: 0.6 }, 0.5)
      .to(subRef.current, { opacity: 1, duration: 0.5 }, 0.65)
      .to(infoRef.current, { opacity: 1, x: 0, duration: 0.7 }, 0.75)
      .to(formRef.current, { opacity: 1, x: 0, duration: 0.7 }, 0.9);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 1200));
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <>
      <div className="contact-page">

        {/* Hero */}
        <div className="contact-hero" ref={heroRef}>
          <div className="contact-hero-bg" />
          <WatermarkSVG />
          <p className="contact-hero-eyebrow" ref={eyebrowRef}>We'd Love to Hear From You</p>
          <h1 className="contact-hero-title" ref={titleRef}>Get in <em>Touch</em></h1>
          <div className="contact-hero-rule" ref={ruleRef} />
          <p className="contact-hero-sub" ref={subRef}>Reach out for bespoke orders, collaborations, or any query</p>
        </div>

        {/* Main Grid */}
        <div className="contact-main">

          {/* Info Column */}
          <div className="contact-info" ref={infoRef}>
            <div className="contact-info-label">Our Details</div>
            <h2 className="contact-info-heading">Crafted with care,<br /><em>answered with warmth</em></h2>
            <p className="contact-info-desc">
              Every query at BEGUM'S deserves a thoughtful response. Whether you seek a bespoke creation, wish to know more about our artisans, or need help with your order — we are here.
            </p>

            <div className="contact-detail-card">
              <div className="contact-detail-icon"><IconLocation /></div>
              <div>
                <div className="contact-detail-label">Our Atelier</div>
                <div className="contact-detail-value">47, Craft Lane, Lajpat Nagar<br />New Delhi — 110024</div>
              </div>
            </div>

            <div className="contact-detail-card">
              <div className="contact-detail-icon"><IconPhone /></div>
              <div>
                <div className="contact-detail-label">Call Us</div>
                <div className="contact-detail-value">+91 98765 43210<br />+91 11 4567 8900</div>
              </div>
            </div>

            <div className="contact-detail-card">
              <div className="contact-detail-icon"><IconMail /></div>
              <div>
                <div className="contact-detail-label">Write to Us</div>
                <div className="contact-detail-value">hello@begums.in<br />orders@begums.in</div>
              </div>
            </div>

            <div className="contact-hours">
              <div className="contact-hours-title">Atelier Hours</div>
              <div className="contact-hours-row"><span>Monday – Friday</span><span>10:00 – 19:00</span></div>
              <div className="contact-hours-row"><span>Saturday</span><span>10:00 – 17:00</span></div>
              <div className="contact-hours-row"><span>Sunday</span><span>By Appointment</span></div>
            </div>
          </div>

          {/* Form Column */}
          <div className="contact-form-wrap" ref={formRef}>
            <div className="contact-form-eyebrow">Send a Message</div>
            <h2 className="contact-form-title">How can we assist you?</h2>

            <form onSubmit={handleSubmit} noValidate>
              <div className="contact-form-row">
                <div className="contact-field">
                  <label className="contact-label">First Name <span style={{color:"#dc3545"}}>*</span></label>
                  <input className="contact-input" name="firstName" placeholder="Priya" value={formData.firstName} onChange={handleChange} required />
                </div>
                <div className="contact-field">
                  <label className="contact-label">Last Name</label>
                  <input className="contact-input" name="lastName" placeholder="Sharma" value={formData.lastName} onChange={handleChange} />
                </div>
              </div>

              <div className="contact-form-row">
                <div className="contact-field">
                  <label className="contact-label">Email <span style={{color:"#dc3545"}}>*</span></label>
                  <input className="contact-input" type="email" name="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} required />
                </div>
                <div className="contact-field">
                  <label className="contact-label">Phone</label>
                  <input className="contact-input" type="tel" name="phone" placeholder="+91 98765 43210" value={formData.phone} onChange={handleChange} />
                </div>
              </div>

              <div className="contact-field">
                <label className="contact-label">Subject <span style={{color:"#dc3545"}}>*</span></label>
                <div className="contact-select-wrap">
                  <select className="contact-select" name="subject" value={formData.subject} onChange={handleChange} required>
                    <option value="">Select a subject</option>
                    <option>Bespoke Order Enquiry</option>
                    <option>Order Tracking</option>
                    <option>Returns & Exchange</option>
                    <option>Wholesale & Collaboration</option>
                    <option>General Query</option>
                  </select>
                </div>
              </div>

              <div className="contact-field">
                <label className="contact-label">Message <span style={{color:"#dc3545"}}>*</span></label>
                <textarea className="contact-textarea" name="message" placeholder="Tell us how we can help..." value={formData.message} onChange={handleChange} required />
              </div>

              <button className="contact-submit" type="submit" disabled={submitting}>
                {submitting ? "Sending..." : "Send Message"}
              </button>

              {submitted && (
                <div className="contact-success">
                  ✦ &nbsp; Thank you. We'll be in touch within 24 hours.
                </div>
              )}
            </form>
          </div>

        </div>
      </div>
    </>
  );
}